import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Mail, Lock, ArrowRight, ArrowLeft, Check, CheckCircle2 } from 'lucide-react';
import { AuthShell, HudLabel, HudInput, PrimaryButton, SocialButton, AuthNotice, C } from '@/components/auth/AuthUI';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatApiErrorDetail(detail) {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).filter(Boolean).join(' ');
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
}

export default function LoginPage() {
  const navigate = useNavigate();
  const [mode, setMode] = useState('login'); // 'login' | 'forgot' | 'forgotSent'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [googleBusy, setGoogleBusy] = useState(false);
  const [appleBusy, setAppleBusy] = useState(false);

  const [fpEmail, setFpEmail] = useState('');
  const [fpMasked, setFpMasked] = useState('');
  const [fpLink, setFpLink] = useState(null);

  const clear = () => { setError(''); setNotice(''); };

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password) return;
    setBusy(true); clear();
    try {
      await axios.post(`${API}/auth/login`, { email: email.trim().toLowerCase(), password, remember }, { withCredentials: true });
      navigate('/dashboard');
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || 'Invalid email or password');
      setBusy(false);
    }
  };

  const googleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    setGoogleBusy(true);
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  const appleSignIn = () => { clear(); setAppleBusy(true); setTimeout(() => { setNotice('Sign in with Apple opens in the Homegrown iOS app.'); setAppleBusy(false); }, 550); };

  const requestReset = async (e) => {
    e.preventDefault();
    if (!fpEmail.trim()) return;
    setBusy(true); clear();
    try {
      const r = await axios.post(`${API}/auth/forgot-password`, { email: fpEmail.trim().toLowerCase() },
        { withCredentials: true, headers: { Origin: window.location.origin } });
      setFpMasked(r.data?.masked_email || '');
      setFpLink(r.data?.reset_link || null); // present only in preview (email not live yet)
      setMode('forgotSent');
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't start the reset. Please try again.");
    } finally { setBusy(false); }
  };

  const goLogin = () => { setMode('login'); clear(); };

  return (
    <AuthShell
      subtitle={mode === 'login' ? 'Sign in to your garden' : mode === 'forgot' ? "We'll email you a secure reset link" : 'Check your inbox'}
      logoTestId="login-sprout"
      onLogoActivate={(e) => { if (e.detail === 2) navigate('/founder'); }}
    >
      {notice ? <div className="mb-4"><AuthNotice kind="success" dataTestid="login-notice"><span className="inline-flex items-center gap-2"><CheckCircle2 className="w-4 h-4" />{notice}</span></AuthNotice></div> : null}

      {mode === 'login' && (
        <form onSubmit={submit} className="space-y-4">
          <div>
            <HudLabel>Email</HudLabel>
            <HudInput icon={Mail} type="email" autoComplete="email" placeholder="you@example.com"
              value={email} onChange={(e) => setEmail(e.target.value)} dataTestid="login-email-input" />
          </div>
          <div>
            <HudLabel>Password</HudLabel>
            <HudInput icon={Lock} secure autoComplete="current-password" placeholder="••••••••"
              value={password} onChange={(e) => setPassword(e.target.value)} dataTestid="login-password-input" />
          </div>

          <div className="flex items-center justify-between pt-0.5">
            <button type="button" onClick={() => setRemember((r) => !r)} data-testid="login-remember-toggle"
              className="inline-flex items-center gap-2 text-sm" style={{ color: 'rgba(250,246,236,0.75)' }}>
              <span className="flex items-center justify-center rounded-md transition-all" style={{
                width: 20, height: 20, background: remember ? C.lime : 'transparent',
                border: `1.5px solid ${remember ? C.lime : 'rgba(250,246,236,0.35)'}` }}>
                {remember ? <Check className="w-3.5 h-3.5" style={{ color: C.forestDeep }} /> : null}
              </span>
              Remember me
            </button>
            <button type="button" data-testid="login-forgot-password-link"
              onClick={() => { setMode('forgot'); setFpEmail(email); clear(); }}
              className="text-sm font-semibold hover:underline" style={{ color: C.lime }}>Forgot my password?</button>
          </div>

          {error ? <AuthNotice dataTestid="login-error"><span className="inline-flex items-center gap-2"><Lock className="w-4 h-4" />{error}</span></AuthNotice> : null}

          <PrimaryButton type="submit" disabled={busy || !email.trim() || !password} dataTestid="login-submit-button">
            <Mail className="w-4 h-4" /> {busy ? 'Signing in…' : 'Sign in with email'}
          </PrimaryButton>

          <div className="flex items-center gap-3 py-1">
            <span className="flex-1 h-px" style={{ background: 'rgba(250,246,236,0.14)' }} />
            <span className="text-xs" style={{ color: 'rgba(250,246,236,0.45)' }}>or continue with</span>
            <span className="flex-1 h-px" style={{ background: 'rgba(250,246,236,0.14)' }} />
          </div>
          <div className="space-y-2.5">
            <SocialButton provider="apple" label="Sign in with Apple" onClick={appleSignIn} loading={appleBusy} dataTestid="login-apple-button" />
            <SocialButton provider="google" label="Sign in with Google" onClick={googleSignIn} loading={googleBusy} dataTestid="login-google-button" />
          </div>

          <div className="pt-2 text-center">
            <p className="text-sm mb-2.5" style={{ color: 'rgba(250,246,236,0.65)' }}>New to Homegrown?</p>
            <button type="button" onClick={() => navigate('/signup')} data-testid="login-create-account-link"
              className="w-full inline-flex items-center justify-center gap-2 rounded-full py-3.5 font-bold text-[15px] transition-all active:scale-[0.97]"
              style={{ background: 'rgba(180,211,48,0.08)', color: C.lime, border: `1.5px solid ${C.lime}` }}>
              Create an account <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </form>
      )}

      {mode === 'forgot' && (
        <form onSubmit={requestReset} className="space-y-4">
          <div>
            <HudLabel>Email</HudLabel>
            <HudInput icon={Mail} type="email" autoComplete="email" placeholder="you@example.com"
              value={fpEmail} onChange={(e) => setFpEmail(e.target.value)} dataTestid="forgot-email-input" />
          </div>
          {error ? <AuthNotice dataTestid="login-error"><span className="inline-flex items-center gap-2"><Lock className="w-4 h-4" />{error}</span></AuthNotice> : null}
          <PrimaryButton type="submit" disabled={busy || !fpEmail.trim()} dataTestid="fp-request-button">
            {busy ? 'Sending…' : 'Email me a reset link'}
          </PrimaryButton>
          <button type="button" onClick={goLogin} className="w-full inline-flex items-center justify-center gap-1.5 text-sm pt-1"
            style={{ color: 'rgba(250,246,236,0.65)' }}><ArrowLeft className="w-4 h-4" /> Back to sign in</button>
        </form>
      )}

      {mode === 'forgotSent' && (
        <div className="space-y-5 text-center" data-testid="fp-sent">
          <div className="mx-auto flex items-center justify-center rounded-full" style={{ width: 60, height: 60, background: 'rgba(180,211,48,0.16)', border: `1px solid ${C.lime}` }}>
            <Mail className="w-6 h-6" style={{ color: C.lime }} />
          </div>
          <div>
            <h2 className="text-xl font-bold" style={{ fontFamily: 'Outfit, sans-serif', color: C.cream }}>Check your email</h2>
            <p className="mt-2 text-sm" style={{ color: 'rgba(250,246,236,0.7)' }}>
              If an account exists{fpMasked ? ` for ${fpMasked}` : ''}, we've sent a secure reset link. It expires in 30 minutes.
            </p>
          </div>
          {fpLink ? (
            <div className="rounded-2xl p-4 text-left" style={{ background: 'rgba(231,182,75,0.12)', border: '1px solid rgba(231,182,75,0.3)' }}>
              <p className="text-xs mb-2" style={{ color: C.amber }}>Preview mode (email not live yet) — open your reset link:</p>
              <button data-testid="fp-dev-link" onClick={() => { const u = new URL(fpLink); navigate(u.pathname + u.search); }}
                className="text-sm font-bold break-all hover:underline" style={{ color: C.lime }}>Open reset screen →</button>
            </div>
          ) : null}
          <button type="button" onClick={goLogin} className="w-full inline-flex items-center justify-center gap-1.5 text-sm"
            style={{ color: 'rgba(250,246,236,0.65)' }}><ArrowLeft className="w-4 h-4" /> Back to sign in</button>
        </div>
      )}
    </AuthShell>
  );
}
